import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Award, Coins, Gift, PartyPopper, ShoppingBag, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { EmptyState, ErrorState, Loading } from "@/components/States";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function StudentRewards() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/student/rewards");
      setData(res.data);
    } catch (err) {
      setError(apiError(err, "No pudimos cargar tus premios."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const redeem = async (rewardId) => {
    setBusy(true);
    try {
      await api.post(`/student/rewards/${rewardId}/redeem`);
      toast.success("¡Pedido enviado! Tu familia lo aprobara.");
      await Promise.all([load(), refresh()]);
    } catch (err) {
      toast.error(apiError(err, "No pudimos canjear."));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading />;

  if (!data.gaming_mode) {
    return (
      <EmptyState
        title="Modo juego desactivado"
        description="Tu familia desactivo las monedas. Tu progreso y tus practicas siguen funcionando igual."
        action={<Link to="/s"><Button className="play-button estudia-touch rounded-2xl bg-violet-600 font-extrabold hover:bg-violet-500">Seguir estudiando</Button></Link>}
        testId="rewards-gaming-off"
      />
    );
  }

  return (
    <div className="student-page" data-testid="student-rewards">
      <div className="play-card relative flex min-h-[230px] items-center justify-between gap-5 overflow-hidden bg-gradient-to-r from-amber-300 to-orange-400 p-6 sm:p-8">
        <span className="play-doodle -right-10 -top-12 h-48 w-48 border-[30px] border-white/20" />
        <div className="relative z-10">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[.16em] text-amber-950/60"><PartyPopper className="h-5 w-5" /> Tu tesoro</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-slate-900">¡Tus monedas brillan!</h1>
          <p className="mt-2 font-display text-6xl font-extrabold text-violet-700" data-testid="rewards-balance">
            {data.coins_balance}
          </p>
        </div>
        <span className="play-icon relative z-10 h-24 w-24 bg-violet-600"><Coins className="h-14 w-14 animate-wiggle" /></span>
      </div>

      <section data-testid="rewards-catalog">
        <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold text-slate-900"><span className="play-icon h-12 w-12 bg-pink-500"><ShoppingBag className="h-7 w-7" /></span> Tienda de premios</h2>
        {data.rewards.length === 0 ? (
          <EmptyState title="Sin premios todavia" description="Tu familia puede crear premios para ti. Mientras tanto, cada práctica sigue mejorando tu progreso." action={<Link to="/s"><Button className="play-button estudia-touch rounded-2xl bg-violet-600 font-extrabold hover:bg-violet-500">Elegir una misión</Button></Link>} />
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {data.rewards.map((reward) => {
              const affordable = data.coins_balance >= reward.cost_coins;
              const eligible = reward.eligible !== false;
              return (
                <div key={reward.id} className="play-card bg-white p-5" data-testid="student-reward-card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <span className="play-icon mb-4 h-14 w-14 bg-pink-500"><Gift className="h-8 w-8" /></span>
                      <p className="font-display text-xl font-extrabold text-slate-900">{reward.name}</p>
                      {reward.description && <p className="text-sm text-slate-500">{reward.description}</p>}
                      {!eligible && <p className="mt-2 text-xs font-extrabold text-violet-600">Te falta: {(reward.requirements || []).join(" · ")}</p>}
                    </div>
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-sm font-bold text-amber-700">
                      <Gift className="h-3.5 w-3.5" /> {reward.cost_coins}
                    </span>
                  </div>
                  <Button
                    onClick={() => redeem(reward.id)}
                    disabled={!affordable || !eligible || busy}
                    className="play-button estudia-touch mt-4 w-full rounded-2xl bg-violet-600 font-extrabold hover:bg-violet-500 disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
                    data-testid={`redeem-btn-${reward.id}`}
                  >
                    {!eligible ? "Completa el reto" : affordable ? "Canjear" : `Te faltan ${reward.cost_coins - data.coins_balance}`}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {data.achievements.length > 0 && (
        <section className="play-card bg-lime-200 p-5" data-testid="student-achievements">
          <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold text-slate-900">
            <span className="play-icon h-12 w-12 bg-emerald-700"><Award className="h-7 w-7" /></span> Insignias desbloqueadas
          </h2>
          <ul className="mt-3 flex flex-wrap gap-2">
            {data.achievements.map((achievement) => (
              <li
                key={achievement.code}
                className="flex items-center gap-1.5 rounded-full border-2 border-emerald-200 bg-white px-3 py-2 text-xs font-extrabold text-emerald-700 shadow-sm"
              >
                <Sparkles className="h-4 w-4" /> {achievement.label}
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.redemptions.length > 0 && (
        <section className="play-card bg-cyan-50 p-5" data-testid="student-redemptions">
          <h2 className="font-display text-lg font-bold text-slate-900">Tus canjes</h2>
          <ul className="mt-3 divide-y divide-slate-100 text-sm">
            {data.redemptions.map((red) => (
              <li key={red.id} className="flex items-center justify-between gap-3 py-2">
                <span className="truncate font-medium text-slate-700">{red.reward}</span>
                <span className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                  {red.status}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="play-card bg-white p-5" data-testid="student-ledger">
        <h2 className="font-display text-lg font-bold text-slate-900">Historial</h2>
        <ul className="mt-3 divide-y divide-slate-100 text-sm">
          {data.ledger.map((movement, index) => (
            <li key={index} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0 truncate text-slate-700">{movement.reason}</span>
              <span
                className={`shrink-0 font-display font-bold ${
                  movement.amount > 0 ? "text-emerald-600" : "text-rose-600"
                }`}
              >
                {movement.amount > 0 ? "+" : ""}
                {movement.amount}
              </span>
            </li>
          ))}
          {data.ledger.length === 0 && <li className="py-2 text-slate-600">Sin movimientos.</li>}
        </ul>
      </section>
    </div>
  );
}
